import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Panel } from '../../../components/Panel'
import { DeskFrame } from './DeskFrame'
import { NotYetLive } from './NotYetLive'
import { useDeskState } from './useDeskState'
import { Failed, Loading, WhoBar, WhoLegend } from './ArticleBits'
import { deskApi, type ArticleRow, type DeskPub } from '../../../api/visitorsDesk'
import {
  RANKING_BUTTONS, articleFiltersFromSearch, metaLine, mono, rankingFromSearch, td, th, withArticleFilters, type ArticleFilters,
} from '../../../lib/deskArticles'
import { deskDates } from '../../../lib/deskPeriod'
import { CREDIT_TODAY_NOTE, creditText, fmtDay, rateWithCount } from '../../../lib/deskFormat'
import { fmtInt } from '../../../lib/format'
import { todayEastern } from '../../../lib/visitorsRange'

const sel = 'min-h-[44px] md:min-h-[32px] bg-ink-900 border border-ink-500 px-2 font-mono text-[12px] text-paper'
const FILTER_NAMES = { section: 'Section', published: 'Published', access: 'Access' } as const

type PubPreset = 'any' | 'period' | 'old'

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

export function DeskArticles() {
  return <DeskFrame tab="articles"><Body /></DeskFrame>
}

type Q = ReturnType<typeof useDeskState>['q']

function Body() {
  const { pub, q, spKey, setSp } = useDeskState('articles')
  const p = pub as DeskPub
  const sp = new URLSearchParams(spKey)
  const ranking = rankingFromSearch(sp)
  const f = articleFiltersFromSearch(sp)
  const fkey = JSON.stringify(f)
  const key = [pub, q.period, q.from, q.to, q.compare, q.group]
  const today = todayEastern()
  const live = q.period === 'today'
  const dates = deskDates(q, today)

  const head = useQuery({
    queryKey: ['desk', 'articles', ...key, ranking, fkey],
    queryFn: () => deskApi.articles(p, q, { ranking, ...f }),
    retry: false, placeholderData: (prev) => prev,
  })
  // The section list feeds the Section filter and the Sections panel's default view.
  const sections = useQuery({ queryKey: ['desk', 'article-sections', ...key, 'section'], queryFn: () => deskApi.articleSections(p, q, 'section'), retry: false })

  const setRanking = (r: string) => { const n = new URLSearchParams(spKey); n.set('ranking', r); setSp(n) }
  const setFilters = (next: ArticleFilters) => setSp(withArticleFilters(spKey, next))
  const preset: PubPreset = !f.published_from && !f.published_to ? 'any'
    : f.published_from === dates.from && f.published_to === dates.to ? 'period'
      : f.published_to && !f.published_from ? 'old' : 'any'
  const onPreset = (v: PubPreset) => {
    const rest = { ...f }
    delete rest.published_from; delete rest.published_to
    if (v === 'period') setFilters({ ...rest, published_from: dates.from, published_to: dates.to })
    else if (v === 'old') setFilters({ ...rest, published_to: addDays(today, -365) })
    else setFilters(rest)
  }

  if (head.isError) return <Failed what="the articles" />
  if (!head.data) return <Loading />
  const d = head.data
  if (d.unavailable !== undefined) return <NotYetLive text={d.unavailable} />

  const set: string[] = []
  if (f.section) set.push(`${FILTER_NAMES.section}: ${f.section}`)
  if (f.published_from || f.published_to) set.push(`${FILTER_NAMES.published}: ${preset === 'period' ? 'this period' : preset === 'old' ? 'over a year ago' : `${f.published_from ?? ''} to ${f.published_to ?? ''}`}`)
  if (f.access) set.push(`${FILTER_NAMES.access}: ${f.access === 'free' ? 'Free' : 'Members'}`)
  const sectionNames = sections.data && sections.data.unavailable === undefined ? sections.data.rows.map((r) => r.name) : []
  if (f.section && !sectionNames.includes(f.section)) sectionNames.push(f.section)
  const buttons = d.rankings.length ? d.rankings.map((r) => ({ id: r.key, label: r.label })) : RANKING_BUTTONS

  return (
    <div className="space-y-4">
      <Panel label="Articles" accent="amber">
        <div className="space-y-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Rank by">
              {buttons.map((b) => (
                <button key={b.id} type="button" aria-pressed={b.id === ranking} onClick={() => setRanking(b.id)}
                        className={`min-h-[40px] border px-3 text-[13px] ${b.id === ranking ? 'border-amber-glow bg-ink-700 text-paper' : 'border-ink-500 text-paper-dim hover:text-paper'}`}>{b.label}</button>
              ))}
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex flex-col gap-1">
                <span className="label">Section</span>
                <select aria-label="Section" className={sel} value={f.section ?? ''} onChange={(e) => setFilters({ ...f, section: e.target.value || undefined })}>
                  <option value="">All sections</option>
                  {sectionNames.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="label">Published</span>
                <select aria-label="Published" className={sel} value={preset} onChange={(e) => onPreset(e.target.value as PubPreset)}>
                  <option value="any">Any time</option>
                  <option value="period">This period</option>
                  <option value="old">Over a year ago</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="label">Access</span>
                <select aria-label="Access" className={sel} value={f.access ?? ''} onChange={(e) => setFilters({ ...f, access: (e.target.value || undefined) as ArticleFilters['access'] })}>
                  <option value="">Free and members</option>
                  <option value="free">Free</option>
                  <option value="members">Members</option>
                </select>
              </label>
            </div>
          </div>
          <p className="text-[12px] text-paper-faint">{d.ranking_note}</p>
          <WhoLegend />
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-[13px]">
              <thead>
                <tr>
                  <th className={th}>Article</th><th className={`${th} text-right`}>People</th><th className={`${th} text-right`}>Real reads</th>
                  <th className={`${th} text-right`}>Read to the end</th><th className={`${th} text-right`}>Read another page</th>
                  <th className={`${th} text-right`}>Arrived here first</th><th className={`${th} text-right`}>Hit paywall</th>
                  <th className={`${th} text-right`}>Members gained</th><th className={th}>Who read it</th>
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r) => <Row key={`${r.publication}-${r.article_id}`} r={r} pub={pub} spKey={spKey} live={live} />)}
                {d.rows.length === 0 && <tr><td colSpan={9} className={`${td} text-paper-faint`}>No articles match in this period</td></tr>}
              </tbody>
            </table>
          </div>
          <p className="text-[12px] text-paper-faint">{d.note}</p>
          {live && <p className="font-mono text-[12px] text-paper-faint">{CREDIT_TODAY_NOTE}</p>}
        </div>
      </Panel>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Sections p={p} q={q} keyBase={key} set={set} live={live} />
        <Old p={p} q={q} keyBase={key} set={set} />
      </div>
    </div>
  )
}

function Row({ r, pub, spKey, live }: { r: ArticleRow; pub: string; spKey: string; live: boolean }) {
  const title = r.title ?? `Article ${r.article_id}`
  const dest = `/visitors/desk/${pub === 'all' ? r.publication : pub}/articles/${r.article_id}${spKey ? `?${spKey}` : ''}`
  return (
    <tr className="border-t border-ink-600">
      <td className={td}>
        <Link to={dest} title={title} className="block max-w-[320px] truncate font-semibold text-amber-glow hover:underline">{title}</Link>
        <div className="text-[12px] text-paper-faint">{metaLine(r)}</div>
      </td>
      <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.people)}</td>
      <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.real_reads)}</td>
      <td className={`${td} text-right font-mono`} style={mono} title={`of ${fmtInt(r.views)} views`}>{rateWithCount(r.read_end_rate, r.read_end)}</td>
      <td className={`${td} text-right font-mono`} style={mono}>{r.recirculation_rate === null ? '—' : rateWithCount(r.recirculation_rate, Math.round(r.recirculation_rate * r.views))}</td>
      <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.entries)}</td>
      <td className={`${td} text-right font-mono`} style={mono}>{r.access === 'free' && r.paywall_hits === 0 ? '—' : rateWithCount(r.paywall_rate, r.paywall_hits)}</td>
      <td className={`${td} text-right font-mono font-semibold`} style={mono} title={live ? 'credited overnight' : undefined}>{creditText(r.members_gained, live)}</td>
      <td className={td}><WhoBar who={r.who} /></td>
    </tr>
  )
}

/** Grey tags for the panels that do not re-query on the table's own filters. */
function NotFiltered({ set }: { set: string[] }) {
  if (set.length === 0) return null
  return (
    <div className="flex flex-wrap gap-2">
      {set.map((s) => <span key={s} className="border border-ink-500 px-1.5 py-0.5 font-mono text-[11px] text-paper-faint">Not filtered by {s}</span>)}
    </div>
  )
}

function Sections({ p, q, keyBase, set, live }: { p: DeskPub; q: Q; keyBase: unknown[]; set: string[]; live: boolean }) {
  const { spKey, setSp } = useDeskState('articles')
  const by = new URLSearchParams(spKey).get('by') === 'tag' ? 'tag' : 'section'
  const query = useQuery({ queryKey: ['desk', 'article-sections', ...keyBase, by], queryFn: () => deskApi.articleSections(p, q, by), retry: false, placeholderData: (prev) => prev })
  const d = query.data
  const pick = (v: 'section' | 'tag') => { const n = new URLSearchParams(spKey); n.set('by', v); setSp(n) }
  return (
    <Panel label="Sections & topics · what to write more of" accent="cyan">
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Group by">
          {(['section', 'tag'] as const).map((v) => (
            <button key={v} type="button" aria-pressed={by === v} onClick={() => pick(v)}
                    className={`min-h-[40px] border px-3 text-[13px] ${by === v ? 'border-amber-glow bg-ink-700 text-paper' : 'border-ink-500 text-paper-dim hover:text-paper'}`}>{v === 'section' ? 'Section' : 'Tag'}</button>
          ))}
        </div>
        <NotFiltered set={set} />
        {query.isError ? <Failed what="sections" /> : !d ? <Loading /> : d.unavailable !== undefined ? <p className="text-paper">{d.unavailable}</p> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] text-[13px]">
              <thead><tr><th className={th}>Section / topic</th><th className={`${th} text-right`}>Articles</th><th className={`${th} text-right`}>People per article</th><th className={`${th} text-right`}>Read to end</th><th className={`${th} text-right`}>Members gained</th></tr></thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={r.name} className="border-t border-ink-600">
                    <td className={td}><span className="block max-w-[200px] truncate" title={r.name}>{r.name}</span></td>
                    <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.articles)}</td>
                    <td className={`${td} text-right font-mono`} style={mono} title={`${fmtInt(r.people)} people in all`}>{fmtInt(Math.round(r.people_per_article))}</td>
                    <td className={`${td} text-right font-mono`} style={mono}>{r.read_end_rate === null ? '—' : `${Math.round(r.read_end_rate * 100)}%`}</td>
                    <td className={`${td} text-right font-mono`} style={mono} title={live ? 'credited overnight' : undefined}>{creditText(r.members_gained, live)}</td>
                  </tr>
                ))}
                {d.rows.length === 0 && <tr><td colSpan={5} className={`${td} text-paper-faint`}>No articles in this period</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Panel>
  )
}

function Old({ p, q, keyBase, set }: { p: DeskPub; q: Q; keyBase: unknown[]; set: string[] }) {
  const { pub, spKey } = useDeskState('articles')
  const query = useQuery({ queryKey: ['desk', 'articles-old', ...keyBase], queryFn: () => deskApi.articlesOld(p, q), retry: false })
  const d = query.data
  return (
    <Panel label="Old articles still pulling readers" accent="green">
      <div className="space-y-2">
        <NotFiltered set={set} />
        {query.isError ? <Failed what="old articles" /> : !d ? <Loading /> : d.unavailable !== undefined ? <p className="text-paper">{d.unavailable}</p> : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[420px] text-[13px]">
                <thead><tr><th className={th}>Article</th><th className={th}>Published</th><th className={`${th} text-right`}>People</th><th className={th}>Mostly from</th></tr></thead>
                <tbody>
                  {d.rows.map((r) => {
                    const title = r.title ?? `Article ${r.article_id}`
                    return (
                      <tr key={`${r.publication}-${r.article_id}`} className="border-t border-ink-600">
                        <td className={td}>
                          <Link to={`/visitors/desk/${pub === 'all' ? r.publication : pub}/articles/${r.article_id}${spKey ? `?${spKey}` : ''}`} title={title}
                                className="block max-w-[240px] truncate text-amber-glow hover:underline">{title}</Link>
                        </td>
                        <td className={`${td} text-paper-faint`}>{r.published ? fmtDay(r.published, { month: 'short', year: 'numeric' }) : '—'}</td>
                        <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.people)}</td>
                        <td className={`${td} text-paper-faint`}><span className="block max-w-[180px] truncate" title={r.mostly_from ?? ''}>{r.mostly_from ?? '—'}</span></td>
                      </tr>
                    )
                  })}
                  {d.rows.length === 0 && <tr><td colSpan={4} className={`${td} text-paper-faint`}>No old articles in this period</td></tr>}
                </tbody>
              </table>
            </div>
            <p className="text-[12px] text-paper-faint">{d.note}</p>
          </>
        )}
      </div>
    </Panel>
  )
}
